// src/features/documents/hooks/useDocumentReady.ts

import {useAppSelector} from "../redux/hooks";
import {
    selectDocumentById,
    selectDocumentsError,
    selectDocumentsLoading
} from "../redux/api/documents/documentsSelectors";
import {Document} from "../types";

/** Результат работы хука useDocumentReady */
interface UseDocumentReadyResult {
    document: Document | null;
    isLoading: boolean;
    error: string | null;
}

/**
 * Хук, который возвращает информацию о готовности конкретного документа (detail-loaded).
 *  - Не делает никаких dispatch (загрузка инициируется где-то в фоне).
 *  - Если документ не найден / не загружен, isLoading=true, doc=null.
 *  - В случае ошибки — error != null.
 */
export function useDocumentReady(documentId?: string): UseDocumentReadyResult {
    // Всегда вызываем хуки, даже если documentId может быть undefined.
    // Если documentId отсутствует, передадим пустую строку в селектор.
    const doc = useAppSelector((state) =>
        documentId ? selectDocumentById(state, documentId) : undefined
    );
    const documentsLoading = useAppSelector(selectDocumentsLoading);
    const documentsError = useAppSelector(selectDocumentsError);

    // Если documentId не передан, возвращаем результат.
    if (!documentId) {
        return { document: null, isLoading: false, error: 'Документ не выбран' };
    }

    let isLoading = false;
    let error: string | null = null;

    if (documentsError) {
        error = documentsError;
    }

    if (!doc) {
        // Если документа нет в стейте, считаем, что загрузка идет,
        // либо он отсутствует.
        isLoading = documentsLoading;
        if (!isLoading && !error) {
            error = 'Документ не найден (возможно, был удалён или не существует)';
        }
    } else {
        if (!doc.detailsLoaded) {
            isLoading = true;
        }
    }

    return {
        document: doc || null,
        isLoading,
        error
    };
}
